<script setup>
import {
  computed,
  nextTick,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const MAX_LOAD_RETRIES = 4;
const RETRY_DELAY_MS = 1200;
const sourceReloadKey = ref(0);
const loadRetryCount = ref(0);

const timeStampURL = computed(() => {
  if (!attachment.dataUrl) return '';

  let url = '';
  try {
    url = timeStampAppendedURL(attachment.dataUrl);
  } catch {
    const separator = attachment.dataUrl.includes('?') ? '&' : '?';
    url = `${attachment.dataUrl}${separator}t=${Date.now()}`;
  }

  if (!sourceReloadKey.value) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}audio_retry=${sourceReloadKey.value}`;
});

const TRANSCRIPT_PREVIEW_LENGTH = 200;
const isTranscriptExpanded = ref(false);
const isTranscriptLong = computed(
  () => (attachment.transcribedText?.length || 0) > TRANSCRIPT_PREVIEW_LENGTH
);
const displayedTranscript = computed(() => {
  const text = attachment.transcribedText || '';
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

// MediaRecorder-produced WebM/Opus blobs lack a Duration header → <audio>.duration
// resolves to Infinity until we seek past the end, which forces the engine to
// scan the file and compute the real length. Safe no-op for files with a real
// duration already (mp3/m4a/etc).
const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    currentTime.value = 0;
    if (Number.isFinite(el.duration) && el.duration > 0) {
      duration.value = el.duration;
    }
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

const updateDuration = () => {
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }

  if (d > 0) {
    duration.value = d;
    const elapsedTime = Number(audioPlayer.value?.currentTime);
    if (!Number.isFinite(elapsedTime) || elapsedTime > d) {
      audioPlayer.value.currentTime = 0;
      currentTime.value = 0;
    }
  }
};

const onAudioReady = () => {
  loadRetryCount.value = 0;
  updateDuration();
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  updateDuration();
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  const numericTime = Number(time);
  if (!Number.isFinite(numericTime) || numericTime <= 0) return '00:00';
  const minutes = Math.floor(numericTime / 60);
  const seconds = Math.floor(numericTime % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const timeRangeLabel = computed(() => {
  return `${formatTime(currentTime.value)} / ${formatTime(duration.value)}`;
});

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  const player = audioPlayer.value;
  const mediaTime = Number(player?.currentTime);
  const mediaDuration = Number(player?.duration);
  const isTimeInRange =
    !Number.isFinite(mediaDuration) ||
    mediaDuration <= 0 ||
    mediaTime <= mediaDuration;
  currentTime.value =
    Number.isFinite(mediaTime) &&
    mediaTime >= 0 &&
    mediaTime !== Number.MAX_SAFE_INTEGER &&
    isTimeInRange
      ? mediaTime
      : 0;
  if (!duration.value) {
    updateDuration();
  }
};

const retryLoad = () => {
  if (!timeStampURL.value || loadRetryCount.value >= MAX_LOAD_RETRIES) return;

  loadRetryCount.value += 1;
  setTimeout(async () => {
    sourceReloadKey.value += 1;
    await nextTick();
    audioPlayer.value?.load();
  }, RETRY_DELAY_MS * loadRetryCount.value);
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    updateDuration();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    :src="timeStampURL"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onAudioReady"
    @loadeddata="onAudioReady"
    @durationchange="onAudioReady"
    @canplay="onAudioReady"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @error="retryLoad"
  />
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ timeRangeLabel }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ displayedTranscript }}
      <button
        v-if="isTranscriptLong"
        class="block mt-1 p-0 border-0 bg-transparent text-n-slate-11 hover:text-n-slate-12 font-medium"
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{
          isTranscriptExpanded
            ? $t('CONVERSATION.AUDIO.TRANSCRIPT_SHOW_LESS')
            : $t('CONVERSATION.AUDIO.TRANSCRIPT_SHOW_MORE')
        }}
      </button>
    </div>
  </div>
</template>
